import React, { useContext } from 'react';
import c from 'classnames';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormRangeWithInputField } from '@hvcgroep/design-system';

import {
  CostsTable,
  FormPrice,
  ResultsBlock,
} from '../../components';
import {
  ACCURACY_VOLUME,
  DEFAULT_GAS_PRICE,
  MAX_GAS_CONSUMPTION,
  MAX_GAS_PRICE,
  MIN_GAS_CONSUMPTION,
  MIN_GAS_PRICE,
  UNIT_VOLUME,
} from '../../config/constants';
import DataContext from '../../contexts/DataContext';
import tableData, { getCostsVerdict } from '../../helpers/costsTable';
import gtm from '../../../../shared/helpers/gtm';

interface Props {
  className?: string;
}

const HeatCalculator: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  const {
    gasConsumption,
    gasPrice,
    gasGridManagementCost,
    gasFixedCost,
    cvInstallationCost,
    cvMaintenanceCost,
    updateGasConsumption,
    updateGasPrice,
  } = useContext(DataContext);

  const classNames = c(
    'HeatCalculator',
    className,
  );

  return (
    <div className={classNames}>
      <p>
        {t('heat_calculator.paragraphs.intro.without_price_cap')}
      </p>

      <Formik
        initialValues={{
          gasConsumption,
          gasPrice,
        }}
        onSubmit={(values) => {
          updateGasConsumption(Number(values.gasConsumption));
          updateGasPrice(Number(values.gasPrice));
        }}
      >
        {({
          isSubmitting,
          values,
          setSubmitting,
          submitForm,
          setFieldValue,
        }) => (
          <Form>
            <FormRangeWithInputField
              name="gasConsumption"
              label={t('heat_calculator.form.gas_consumption.label')}
              placeholder="0"
              min={MIN_GAS_CONSUMPTION}
              max={MAX_GAS_CONSUMPTION}
              accuracy={ACCURACY_VOLUME}
              unit={UNIT_VOLUME}
              unitAfter
              required
              disabled={isSubmitting}
              onBlur={() => {
                setSubmitting(true);
                submitForm()
                  .then(() => {
                    setSubmitting(false);
                  })
                  .catch((err) => console.log(err));

                // GTM.
                gtm.legacyTrackUserEvent('submitGasConsumption', { gas_consumption: values.gasConsumption });
              }}
            />
            <FormPrice
              name="gasPrice"
              label={t('heat_calculator.form.gas_price.label')}
              description={t('heat_calculator.form.gas_price.description')}
              min={MIN_GAS_PRICE}
              max={MAX_GAS_PRICE}
              required
              disabled={isSubmitting}
              onBlur={() => {
                setSubmitting(true);
                submitForm()
                  .then(() => {
                    setSubmitting(false);
                  })
                  .catch((err) => console.log(err));

                // GTM.
                gtm.legacyTrackUserEvent('submitGasPrice', { gas_price: values.gasPrice });
              }}
            />

            <CostsTable
              title={t('heat_calculator.titles.costs_table')}
              data={tableData(
                gasConsumption,
                gasPrice,
                gasGridManagementCost,
                gasFixedCost,
                cvMaintenanceCost,
                cvInstallationCost,
              )}
            />

            <ResultsBlock
              type={getCostsVerdict(
                gasConsumption,
                gasPrice,
                gasGridManagementCost,
                gasFixedCost,
                cvInstallationCost,
                cvMaintenanceCost,
              )}
              onReset={() => {
                updateGasPrice(DEFAULT_GAS_PRICE);
                setFieldValue('gasPrice', DEFAULT_GAS_PRICE);
              }}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default HeatCalculator;
